import React, { useState, useEffect, useRef } from "react";
import { Menu } from "@headlessui/react";

const MoreHorizontalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

const MoreVerticalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
  >
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

const IconSelector = ({
  menus,
  orientation = "horizontal",
  color = "gray",
  classNames,
}) => {
  const buttonRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  // Determine which icon to use based on orientation
  const Icon =
    orientation === "horizontal" ? MoreHorizontalIcon : MoreVerticalIcon;

  const colorClasses = {
    gray: "text-gray-400 hover:text-gray-600",
    white: "text-white hover:text-gray-200",
  };

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = menus.length * 40 + 16; // Approximate dropdown height
      
      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Position dropdown above if not enough space below
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownStyle({
          bottom: `${viewportHeight - rect.top + 8}px`,
          right: `${window.innerWidth - rect.right}px`,
          top: 'auto',
        });
      } else {
        setDropdownStyle({
          top: `${rect.bottom + 8}px`,
          right: `${window.innerWidth - rect.right}px`,
          bottom: 'auto',
        });
      }
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => {
        if (open) {
          // Update position when dropdown opens
          setTimeout(updateDropdownPosition, 0);
        }
        return (
          <>
            <Menu.Button
              ref={buttonRef}
              className={`p-1 flex items-center rounded-full ${
                colorClasses[color]
              } 
              ${
                open
                  ? "ring-2 ring-warmgray-300"
                  : "focus:ring-2 focus:ring-warmgray-300"
              } 
              focus:outline-none ${classNames}`}
            >
              <Icon aria-hidden="true" />
            </Menu.Button>
            <Menu.Items 
              className={`${open ? 'fixed' : 'absolute'} right-0 z-[9999] mt-2 mb-2 w-auto whitespace-nowrap origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
              style={open ? dropdownStyle : {}}
            >
              <div className="py-1">
                {menus.map((menu, index) => (
                  <Menu.Item key={index}>
                    {({ active }) => (
                      <button
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          active ? "bg-gray-300 text-gray-900" : "text-gray-900"
                        } ${
                          menu.disabled ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        onClick={menu.action}
                        disabled={menu.disabled}
                      >
                        <div className="flex items-center gap-2">
                          {menu.icon && (
                            <menu.icon
                              className={`h-4 w-4 ${
                                menu.disabled ? "text-gray-400" : "text-gray-500"
                              }`}
                            />
                          )}
                          {menu.label}
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </>
        );
      }}
    </Menu>
  );
};

export default IconSelector;
